import React, { useState, useReducer } from "react";
import Dayjs from "dayjs";
import { useParams } from "react-router-dom";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";

// Custom.
import PreviewLayout from "containers/Layout/PreviewLayout";
import { Form, FormTabProps } from "@vocollege/components";
import I18n from "@vocollege/app/dist/modules/Services/I18n";
import {
  GET_TUTOR_AWARD_CERTIFICATE,
  UPDATE_TUTOR_AWARD_CERTIFICATE,
  CREATE_TUTOR_AWARD_CERTIFICATE,
  SEARCH_GROUP,
} from "api";
import { VoAuth } from "@vocollege/app";
import { reducer, getContactName } from "@vocollege/app/dist/modules/VoHelpers";
import { useStyles } from "ui/styles/form";
import TutorAwardCertificatePreview from "containers/Templates/TutorAwardCertificate/TutorAwardCertificatePreview";
import { SEARCH_USER } from "@vocollege/app/dist/modules/VoApi";
import { FormProps } from "@vocollege/components/dist/Form";
import { getUserString } from "@vocollege/app/dist/modules/VoHelpers";
import { getGroupTitle, getGroupTitleValue } from "services/Utils";
import { EnhancedTableStatusCell } from "@vocollege/components";

const TutorAwardCertificateForm: React.FC = () => {
  const initialDate = Dayjs().format("YYYY-MM-DD");
  const urlParams = useParams<any>();
  const classes = useStyles();
  const { id } = urlParams;
  const [formLoading, setFormLoading] = useState(false);
  const [formState, setFormState] = useState({
    isDirty: false,
    isValid: false,
  });
  const navigate = useNavigate();

  // Methods.

  const getInitialState = () => {
    const initialState: { [key: string]: any } = {
      id: 0,
      firstname: "",
      lastname: "",
      year: "",
      date: initialDate,
      place: "",
      text: "",
      president: "",
      author: null,
      college: null,
      done: 0,
    };
    if (id && id === "skapa") {
      initialState.author = {
        id: VoAuth.currentUser.id,
        name: VoAuth.currentUser.name,
        type: "user",
      };
    }
    return initialState;
  };

  const [state, dispatch] = useReducer(reducer, getInitialState());

  const handleCancel = () => {
    navigate("/certifikat-arets-handledare");
  };

  const handleComplete = (obj: any) => {
    navigate(`/certifikat-arets-handledare/${obj[Object.keys(obj)[0]].id}`);
  };

  const handleFormChange: FormProps["onFormChange"] = (formState) => {
    setFormState(formState);
  };

  const getTabs = () => {
    const tabs: FormTabProps[] = [];
    tabs.push({
      key: "content",
      label: I18n.get.misc.content,
      fields: [
        {
          name: "firstname",
          label: I18n.get.user.labels.firstname,
          type: "text",
          required: true,
          grid: {
            xs: 12,
            md: 6,
            lg: 4,
          },
          validation: {
            required: true,
          },
          onChange: (value: any) =>
            dispatch({ key: "firstname", values: value }),
        },
        {
          name: "lastname",
          label: I18n.get.user.labels.lastname,
          type: "text",
          required: true,
          grid: {
            xs: 12,
            md: 6,
            lg: 4,
          },
          validation: {
            required: true,
          },
          onChange: (value: any) =>
            dispatch({ key: "lastname", values: value }),
        },
        {
          name: "year",
          label: I18n.get.misc.year,
          type: "text",
          required: true,
          grid: {
            xs: 12,
            md: 3,
          },
          validation: {
            required: true,
          },
          onChange: (value: any) => dispatch({ key: "year", values: value }),
        },
        {
          name: "date",
          label: I18n.get.misc.date,
          type: "date_field",
          required: true,
          grid: {
            xs: 12,
            md: 4,
          },
          onChange: (value: any) => dispatch({ key: "date", values: value }),
        },
        {
          name: "place",
          label: I18n.get.user.labels.town,
          type: "text",
          required: true,
          grid: {
            xs: 12,
            md: 5,
          },
          validation: {
            required: true,
          },
          onChange: (value: any) => dispatch({ key: "place", values: value }),
        },
        {
          name: "text",
          label: I18n.get.misc.text,
          type: "textarea",
          grid: {
            xs: 12,
          },
          onChange: (value: any) => dispatch({ key: "text", values: value }),
        },

        {
          name: "college",
          label: I18n.get.misc.college,
          type: "entity_field",
          grid: {
            xs: 12,
          },
          params: {
            renderFieldValue: getGroupTitleValue,
            dialog: {
              types: ["group"],
              renderTitleField: getGroupTitle,
              extraDetails: (item: any) =>
                item?.status === 0 && (
                  <EnhancedTableStatusCell data={item.status} />
                ),
              query: SEARCH_GROUP,
              category: "searchGroup",
              variables: {
                categories: ["region", "local"],
              },
            },
          },
          onChange: (value: any) => dispatch({ key: "college", values: value }),
        },

        {
          name: "president",
          label: I18n.get.tutorAwardCertificate.labels.president,
          type: "text",
          required: true,
          validation: {
            required: true,
          },
          grid: {
            xs: 12,
          },
          onChange: (value: any) =>
            dispatch({ key: "president", values: value }),
        },

        {
          name: "author",
          label: I18n.get.misc.author,
          type: "entity_field",
          grid: {
            xs: 12,
          },
          params: {
            dialog: {
              renderTitleField: (item: any) => getUserString(item),
              types: ["user"],
              query: SEARCH_USER,
              category: "searchUser",
            },
            fields: {
              user: {
                title: "name",
              },
            },
          },
        },
      ],
    });
    return tabs;
  };

  return (
    <PreviewLayout
      className={classes.root}
      leftColumn={
        <>
          <Alert severity="warning" className={classes.row}>
            {I18n.get.vocertMessages.correctPreview}
          </Alert>
          {!state.uid && !formLoading && (
            <Alert severity="error" className={classes.row}>
              {I18n.get.diploma.messages.saveBeforePrint}
            </Alert>
          )}
          <Form
            tabs={getTabs()}
            labels={{
              loading: I18n.get.form.labels.loadingContent,
              create:
                I18n.get.tutorAwardCertificate.labels
                  .createTutorAwardCertificate,
              created:
                I18n.get.tutorAwardCertificate.messages
                  .tutorAwardCertificateSaved,
              updated:
                I18n.get.tutorAwardCertificate.messages
                  .tutorAwardCertificateSaved,
              deleted:
                I18n.get.tutorAwardCertificate.messages
                  .tutorAwardCertificateDeleted,
              fieldRequired: I18n.get.form.errors.required,
            }}
            operations={{
              category: "certTutorAwardCertificate",
              get: GET_TUTOR_AWARD_CERTIFICATE,
              create: CREATE_TUTOR_AWARD_CERTIFICATE,
              update: UPDATE_TUTOR_AWARD_CERTIFICATE,
            }}
            paths={{
              back: "/certifikat-arets-handledare",
            }}
            initialState={getInitialState()}
            renderPageTitle={(state) => {
              if (state.id === 0) {
                return I18n.get.tutorAwardCertificate.labels
                  .createTutorAwardCertificate;
              }
              return getContactName(state);
            }}
            urlParams={urlParams}
            createParam="skapa"
            classes={{
              toolbar: classes.toolbar,
              paper: classes.form,
            }}
            onFormChange={handleFormChange}
            onQueryLoading={(loading) => setFormLoading(loading)}
            onDataChange={(data, fetchedData) => {
              dispatch({
                values: {
                  ...data,
                  uid: fetchedData.certTutorAwardCertificate.uid,
                },
              });
            }}
            onComplete={handleComplete}
            onCancel={handleCancel}
            loadQueryOnParamsChange
          />
        </>
      }
      rightColumn={
        !formLoading ? (
          <TutorAwardCertificatePreview
            item={state}
            formState={formState}
            id={id}
          />
        ) : (
          <></>
        )
      }
      rightColumnProps={{
        className: clsx(classes.previewWrapper),
      }}
    />
  );
};

export default TutorAwardCertificateForm;
